import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  CheckCircle,
  Error,
  PersonAdd,
  Home
} from '@mui/icons-material';
import { authService } from '@services/authService';

const ShelterStaffActivate: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: ''
  });

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Token de activare invalid sau lipsă');
    }
  }, [token]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (formData.password !== formData.confirmPassword) {
      setError('Parolele nu se potrivesc');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Parola trebuie să aibă cel puțin 6 caractere');
      setLoading(false);
      return;
    }

    try {
      const response = await authService.activateStaffAccount({
        token: token!,
        firstName: formData.firstName,
        lastName: formData.lastName,
        password: formData.password
      });

      if (response.success) {
        setSuccess('Contul a fost activat cu succes! Te poți conecta acum.');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(response.error || 'Eroare la activarea contului');
      }
    } catch (error: any) {
      setError(error.message || 'Eroare la activarea contului');
    } finally {
      setLoading(false);
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };

  if (!token) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box textAlign="center">
            <Error color="error" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h4" gutterBottom color="error">
              Token Invalid
            </Typography>
            <Typography variant="body1" paragraph>
              Token-ul de activare este invalid sau lipsă.
            </Typography>
            <Button
              variant="contained"
              startIcon={<Home />}
              onClick={handleGoHome}
              sx={{ mt: 2 }}
            >
              Mergi la Pagina Principală
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box textAlign="center">
            <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h4" gutterBottom color="success">
              Cont Activat!
            </Typography>
            <Typography variant="body1" paragraph>
              {success}
            </Typography>
            <Button
              variant="contained"
              startIcon={<Home />}
              onClick={handleGoHome}
              sx={{ mt: 2 }}
            >
              Mergi la Pagina Principală
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box textAlign="center" mb={4}>
          <PersonAdd color="primary" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Activează Contul de Staff
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Completează informațiile pentru a-ți activa contul în echipa adapostului
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              name="firstName"
              label="Prenume"
              value={formData.firstName}
              onChange={handleInputChange}
              required
              fullWidth
              disabled={loading}
            />

            <TextField
              name="lastName"
              label="Nume"
              value={formData.lastName}
              onChange={handleInputChange}
              required
              fullWidth
              disabled={loading}
            />

            <TextField
              name="password"
              label="Parolă"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              required
              fullWidth
              disabled={loading}
              helperText="Minim 6 caractere"
            />

            <TextField
              name="confirmPassword"
              label="Confirmă Parola"
              type="password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
              fullWidth
              disabled={loading}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Se activează...
                </>
              ) : (
                'Activează Contul'
              )}
            </Button>
          </Box>
        </form>

        <Box textAlign="center" mt={4}>
          <Button
            variant="text"
            startIcon={<Home />}
            onClick={handleGoHome}
          >
            Mergi la Pagina Principală
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ShelterStaffActivate;
